import React, { useState, MouseEventHandler } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@tecma/i18n";
import { Button, ButtonGroup, Drawer, Header as TecmaHeader } from "@tecma/ds";
import { AuthUtils } from "@tecma/auth-utils";
import { useDispatch, useSelector } from "react-redux";

import {
  applicationStateSelectors,
  getAvailableLanguagesSelector,
} from "store/slices/applicationState/selectors";
import ROUTE_APP_PATHS from "routes/routesPaths";
import { authStateSelectors } from "store/slices/auth/selectors";
import { getBackUrl } from "utils/detectUrl";

import "./header.scss";
import { authActions } from "store/sagas/auth/auth.actions";
import ContactUsModal from "components/ContactUsModal";

const Header = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [openModalSupport, setOpenModalSupport] = useState(false);
  const availableLanguages = useSelector(getAvailableLanguagesSelector);
  const logoUrl = useSelector(applicationStateSelectors.logo);
  const projectId = useSelector(applicationStateSelectors.projectID) ?? "";
  const userIsLogged = useSelector(authStateSelectors.userIsLogged);
  const userLogged = useSelector(authStateSelectors.userLogged);
  const myAccount = useSelector(applicationStateSelectors.myAccount);
  const myHome = useSelector(applicationStateSelectors.myHome);

  const onLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  const handleModalOpen: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    setOpenModalSupport(!openModalSupport);
  };

  const goToRoomList = () => {
    navigate(ROUTE_APP_PATHS.spaces);
  };

  const goToPersonalArea = () => {
    if (!myHome?.url) return;
    window.open(myHome.url);
  };

  const handleLogin = () => {
    AuthUtils.authenticate({
      projectId,
      redirectUrl: getBackUrl(),
      platform: "COLIVING",
      locale: i18n.language,
      myAccountBaseUrl: myAccount?.url ?? "",
    });
  };

  const handleLogout = () => {
    dispatch(authActions.doLogout());
  };

  return (
    <>
      <TecmaHeader
        logo={logoUrl}
        desktopHeaderContent={
          <>
            {userIsLogged ? (
              <TecmaHeader.Item
                label={`${userLogged?.firstName} ${userLogged?.lastName}`}
                iconName='user-circle'
              >
                <TecmaHeader.MenuItem
                  title={t("header.personal-area")}
                  onClick={goToPersonalArea}
                />
                <TecmaHeader.Divider />
                <TecmaHeader.MenuItem
                  title={t("logout")}
                  onClick={handleLogout}
                />
              </TecmaHeader.Item>
            ) : (
              <TecmaHeader.Item
                onClick={handleLogin}
                iconName='user-circle'
                label={t("header.login")}
              />
            )}
            <TecmaHeader.LanguageSelector
              currentLanguage={i18n.language}
              languages={availableLanguages}
              onChangeLanguage={onLanguageChange}
            />

            <Button iconName='headset' onClick={handleModalOpen}>
              {t("header.support")}
            </Button>
          </>
        }
        mobileHeaderContent={
          <TecmaHeader.Item iconName='headset' onClick={handleModalOpen} />
        }
        mobileDrawerContent={
          <>
            <Drawer.Content>
              {userIsLogged && (
                <Drawer.Avatar
                  avatarProps={{
                    text: `${userLogged?.firstName} ${userLogged?.lastName}`
                      ?.match(/(\b\S)?/g)
                      ?.join("")
                      ?.match(/(^\S|\S$)?/g)
                      ?.join("")
                      ?.toUpperCase(),
                  }}
                  title={`${userLogged?.firstName} ${userLogged?.lastName}`}
                />
              )}

              <div>
                <div className='tecma-drawer-space-button'>
                  <Button iconName='search' onClick={goToRoomList}>
                    {t("header.spaces")}
                  </Button>
                </div>
                {userIsLogged ? (
                  <Drawer.Item
                    label={t("header.personal-area")}
                    iconName='user'
                    onClick={goToPersonalArea}
                  />
                ) : (
                  <Drawer.Item
                    label={t("header.login")}
                    iconName='user-circle'
                    onClick={handleLogin}
                  />
                )}
                <Drawer.Item
                  label={t("header.support")}
                  iconName='headset'
                  onClick={handleModalOpen}
                />
                <Drawer.Item
                  label={t(`language.${i18n.language}.label`)}
                  iconName='translate'
                  rightIcon='chevron-right'
                  menuLayout='page'
                >
                  <Drawer.Languages
                    currentLanguage={i18n.language}
                    onChangeLanguage={onLanguageChange}
                    languages={availableLanguages}
                  />
                </Drawer.Item>
              </div>
            </Drawer.Content>

            {userIsLogged && (
              <Drawer.Footer>
                <Drawer.Item
                  label={t("header.logout")}
                  iconName='logout'
                  onClick={handleLogout}
                />
              </Drawer.Footer>
            )}
          </>
        }
      />
      <ContactUsModal
        isOpen={openModalSupport}
        onClose={() => setOpenModalSupport(false)}
      />
    </>
  );
};

export default Header;
